/**
 * StageScenarioCard — V14.93 : « ce qui va se passer à cette étape » —
 * image d'appui (ancre du lot), variables réellement appliquées, ce qui est figé.
 * Alimenté par `preview-prompt.scenario` + `variables`.
 */
import { Anchor, Lock, Sparkles, AlertTriangle, Pencil } from 'lucide-react';
import { VariableRow } from './PromptVariableRow';

const fullImg = (path) => (path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : null);

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

/**
 * editable + onChange(key, value) + onReset(key) : les variables de l'étape
 * deviennent modifiables en place (V14.94) — même contrôle que le lanceur.
 */
export const StageScenarioCard = ({ scenario, variables = [], compact = false, onEdit, editable = false, onChange, onReset }) => {
  if (!scenario) return null;
  const keys = new Set(scenario.keys || []);
  const applied = variables.filter((v) => keys.has(v.key));
  const adjusted = applied.filter((v) => v.origin === 'lanceur').length;
  const b = scenario.binding || {};
  return (
    <div
      className={`rounded-lg ${compact ? 'p-2.5 space-y-2' : 'p-3 space-y-2.5'}`}
      style={{ background: 'rgba(217,160,91,0.06)', border: '1px solid rgba(217,160,91,0.28)' }}
      data-testid={`stage-scenario-${scenario.stage}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="hud-eyebrow">Scénario · étape {scenario.n}/4</span>
        <span className="text-[11.5px] font-semibold" style={{ color: 'var(--c-text)', fontFamily: 'Outfit, sans-serif' }}>{scenario.title}</span>
        {onEdit && (
          <button type="button" onClick={onEdit} className="ml-auto text-[10.5px] underline hover:opacity-80" style={{ color: 'var(--c-gold)' }}
                  data-testid="stage-scenario-edit">
            Ajuster dans le lanceur
          </button>
        )}
      </div>

      <div className={`grid ${compact ? 'grid-cols-[64px_1fr]' : 'grid-cols-[88px_1fr]'} gap-2.5 items-start`}>
        <div className="aspect-square rounded-md overflow-hidden flex items-center justify-center"
             style={{ background: 'rgba(0,0,0,0.35)', border: `1px solid ${b.ready ? 'rgba(244,114,182,0.45)' : 'rgba(250,204,21,0.4)'}` }}
             data-testid="stage-scenario-binding">
          {b.image_url ? (
            <img src={fullImg(b.image_url)} alt={b.label} className="w-full h-full object-cover" />
          ) : b.ready ? (
            <Sparkles className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
          ) : (
            <AlertTriangle className="w-4 h-4" style={{ color: '#FACC15' }} />
          )}
        </div>
        <div className="min-w-0 space-y-1">
          <div className="text-[10.5px] inline-flex items-center gap-1" style={{ color: b.ready ? '#F9A8D4' : '#FACC15' }}>
            <Anchor className="w-3 h-3" /> Image d'appui : {b.label}
            {b.job_id ? <span style={{ color: 'var(--c-text-faint)' }}>· {b.job_id.replace('gen_', '').slice(0, 6)}{b.vue_angle ? ` · ${b.vue_angle}` : ''}</span> : null}
            {!b.ready && <span style={{ color: '#FACC15' }}>· manquante — validez l'étape précédente</span>}
          </div>
          <div className="text-[10.5px] inline-flex items-center gap-1" style={{ color: 'var(--c-text-2)' }}>
            <Lock className="w-3 h-3" style={{ color: '#38BDF8' }} /> Figé : {scenario.frozen}
          </div>
        </div>
      </div>

      <div data-testid="stage-scenario-vars">
        <div className="text-[9.5px] uppercase tracking-wider mb-1 flex items-center gap-1.5" style={{ color: 'var(--c-text-faint)' }}>
          Paramètres appliqués à cette étape · {applied.length}
          {editable && <Pencil className="w-2.5 h-2.5" style={{ color: 'var(--c-gold)' }} />}
          {editable && adjusted > 0 && (
            <span className="normal-case tracking-normal px-1.5 rounded-full" style={{ background: 'rgba(217,160,91,0.16)', color: 'var(--c-gold)' }} data-testid="stage-scenario-adjusted">
              {adjusted} ajustée{adjusted > 1 ? 's' : ''} · reprise{adjusted > 1 ? 's' : ''} par le lanceur
            </span>
          )}
        </div>
        {applied.length === 0 ? (
          <p className="text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>Aucune variable — géométrie et caméra seules.</p>
        ) : editable ? (
          <div data-testid="stage-scenario-editor">
            {applied.map((v) => <VariableRow key={v.key} v={v} onChange={onChange} onReset={onReset} inStage />)}
          </div>
        ) : (
          <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-2'} gap-x-3 gap-y-0.5`}>
            {applied.map((v) => (
              <div key={v.key} className="flex items-baseline gap-1.5 text-[11px] min-w-0" data-testid={`stage-scenario-var-${v.key}`}>
                <span className="shrink-0 uppercase tracking-wider text-[9.5px]" style={{ color: 'var(--c-text-faint)' }}>{v.label}</span>
                <span className="truncate" style={{ color: v.value === '' || v.value == null ? 'var(--c-text-faint)' : 'var(--c-text)' }} title={fmt(v.value)}>{fmt(v.value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StageScenarioCard;
